import { type MRT_ColumnDef } from "material-react-table";
import { Box, Tooltip, IconButton, Dialog, DialogContent, Typography, LinearProgress, linearProgressClasses, CircularProgress } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import CustomVideoControls from "../MeetingDetails/CustomVideoPlayer";
import DownloadIcon from "../../assets/interaction-detail-page/download-idp.svg";
import Delete from "../../assets/interaction-detail-page/delete-idp.svg";
import FileName from "../../assets/interaction-detail-page/file.svg";
import { aiSupportedExtensions, getFileExtension, audioExtensions, getFileColor, isCondensedTranscript, isGroupAiCompleted, isGroupAiInProgress, normalizeLeadingCapital } from "../../utils/utils";
import AiProcessingIcon from "../../assets/interaction-detail-page/ai-processing-icon.svg";
import ReplayIcon from "@mui/icons-material/Replay";
import EllipsedTooltip from "../Common/EllipsedTooltip";
import { ACTIVE_UPLOAD_STATUSES, sendFileExtensions } from "../../utils/types";
import dayjs from "dayjs";
import "../../styles/common.css";
import AiIcon from "../../assets/interaction-detail-page/ai-processing-complete.svg";
import AiLoadingIcon from "../../assets/interaction-detail-page/ai-processing-load.svg";

export const initialSort = { sort_field: "created_at", sort_order: "asc" };


const getOriginalFileName = (row: any) =>
  row.original?.asset_name || "";

const isAudioRow = (row: any, fileExtension: string) =>
  row.original?.c_type === "audio" || audioExtensions.includes(fileExtension);

export const getColumns = ({
  handleDownload,
  handleOpenVideo,
  handleCloseVideoDialog,
  handleOpenView,
  handleCloseView,
  setDeleteCollateralId,
  setOpenConfirmDialog,
  handlePreview,
  handleOpenAiProcessing,
  onNavigateToRecording,
  loggedInEmail,
  openView,
  isOpenVideoDialog,
  activeMediaFileId,
  videoUrl,
  videoRef,
  transcripts,
  interactionId,
  handleRetry,
  cancelUpload,
  groupStatusMap = {},
  aiProcessingEnabled = false,
  mediaHandlers = {},
  spritesheetDownloadData
}: any): MRT_ColumnDef<any>[] => {
  const renderMediaDialog = (row: any, fileExtension: string, isAudioFile: boolean) => (
    <Dialog
      open={isOpenVideoDialog && (!activeMediaFileId || activeMediaFileId === (row.original?.id || row.id))}
      maxWidth={false}
      sx={{
        "& .MuiDialog-paper": {
          width: isAudioFile ? "500px" : "80%",
          maxWidth: isAudioFile ? "500px" : "600px",
          padding: 2,
        },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6" sx={{ fontFamily: 'var(--font-family)' }}>
          {isAudioFile ? "Audio Player" : ""}
        </Typography>
        <IconButton onClick={handleCloseVideoDialog} sx={{ backgroundColor:"#F2F2F4", ":hover": { backgroundColor: "#F2F2F4" } }}>
          <ClearIcon />
        </IconButton>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 2 }}>
        {fileExtension === "mp4" ? (
          <video
            src={videoUrl}
            ref={videoRef}
            width="100%"
            height="300px"
            {...mediaHandlers}
          />
        ) : (
          <audio
            src={videoUrl}
            ref={videoRef}
            style={{ width: "100%", marginTop: "10px" }}
            {...mediaHandlers}
          />
        )}
        <CustomVideoControls
          videoRef={videoRef}
          spritesheetUrl={spritesheetDownloadData?.data?.downloadUrl}
          interactionDetails={row.original?.interactionDetails}
          interactionId={interactionId}
          assetId={row.original?.id || row?.id}
          isAudio={isAudioFile}
          isClipped={false}
        />
      </Box>
    </Dialog>
  );

  return [
  {
    accessorKey: "name",
    header: "File Name",
    Cell: ({ row }: { row: any }) => {
      const fileUrl =
        row.original?.type === "attachment"
          ? row.original?.url || row.original?.file_name
          : row.original?.file_name;
      const fileName = getOriginalFileName(row);

      const isGoogleDoc = fileUrl?.includes("docs.google.com");
      const fileExtension = !isGoogleDoc ? getFileExtension(fileName) : "";
      const isAudioFile = isAudioRow(row, fileExtension);
      let displayName = fileName;

      const isUploadingRow = Boolean(row.original?.uploadState);

      return (
        <>
        <Tooltip title={displayName}>
          <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            minWidth: 0,
            width: "100%",
            overflow: "hidden",
          }}>
          <Box className="file_name" sx={{ flexShrink: 0 }}>
            <img
              src={FileName}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
            <Box className="file_name_icon"
              sx={{
                backgroundColor: getFileColor(fileExtension),
              }}
            >
              {fileExtension?.toUpperCase()}
            </Box>
          </Box>
          <Box className="truncate-table-cell-ellipsis"
            onClick={() => {
              if (isUploadingRow) return;

              const lowerName = fileName?.toLowerCase() || "";
              const ext = fileExtension;
              if (fileUrl?.includes("docs.google.com/document")) {
                window.open(fileUrl, "_blank", "noopener,noreferrer");
                return;
              }

              const targetAssetId = row.original?.id || row.id;

              if (ext === "mp4" || isAudioFile) {
                handleOpenVideo(targetAssetId);
                return;
              }

              if (ext === "txt" && isCondensedTranscript(lowerName)) {
                handleOpenView(targetAssetId);
                return;
              }

              handlePreview(targetAssetId);
            }}
            sx={{
              color: isUploadingRow ? "#757575" : "#298EC7",
              cursor: isUploadingRow ? "not-allowed" : "pointer",
              "&:hover": { textDecorationLine: isUploadingRow ? "none" : "underline" },
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
            }} 
          >
            {displayName}
          </Box>
          </Box>
        </Tooltip>
        {(fileExtension === "mp4" || isAudioFile) &&
          renderMediaDialog(row, fileExtension, isAudioFile)}
        </>
      );
    },
    size: 500,
    minSize: 500,
    maxSize: 500,
    enableColumnFilter: false,
    grow: true,
    enableSorting: false,
  },
  {
    accessorKey: "upload_date",
    header: "Upload Date",
    size: 100,
    minSize: 100,
    maxSize: 100,
    enableColumnFilter: false,
    enableSorting: false,
    Cell: ({ row }: { row: any }) => {
      const createdAt = row.original?.created_at;

      return (
      <Typography>
        {createdAt ? dayjs(createdAt).format("MMM DD, YYYY") : "-"}
      </Typography>
    );
    },
    grow: true,
  },
  {
    accessorKey: "uploaded_by",
    header: "Uploaded By",
    size: 150,
    minSize: 150,
    maxSize: 150,
    grow: true,
    enableColumnFilter: false,
    enableSorting: false,
    Cell: ({ row }: { row: any }) => {
      const uploadedBy = row.original?.uploaded_by;
      if (!uploadedBy) {
        return <span>&mdash;</span>;
      }
      return (
        <EllipsedTooltip tooltipContent={uploadedBy} maxWidth="160px">
        <span>
          {uploadedBy}
        </span>
        </EllipsedTooltip>
      );
    },
  },
  {
    accessorKey: "asset_source",
    header: "Source",
    size: 150,
    minSize: 150,
    maxSize: 150,
    grow: true,
    enableColumnFilter: false,
    enableSorting: false,
    Cell: ({ cell, row }: { cell: any; row: any }) => {
      const value = cell.getValue() as string | null;
      const creatorEmail = row.original?.creator_email;

      if (value?.includes("7G_Recall")) return <span>7thgear</span>;
      if (!value) return <span>&mdash;</span>;

      if (value.includes("Account") && creatorEmail) {
        const domainParts = creatorEmail.split("@")[1]?.split(".");
        const domain = domainParts?.slice(0, -1).join(".");
        return <span>{normalizeLeadingCapital(domain)}</span>;
      }
      return <span>{value?.replace(/_/g, " ")}</span>;
    },
  },
  {
    accessorKey: "downloadAction",
    header: "Actions",
    size: 150,
    minSize: 150,
    maxSize: 150,
    grow: true,
    enableColumnFilter: false,
    enableSorting: false,
    Cell: ({ row }: { row: any }) => {
      const isInteractionAttendee = row.original?.isInteractionAttendee;
      if (
        row.original?.uploadState &&
        ACTIVE_UPLOAD_STATUSES.includes(row.original.uploadState.status)
      ) {
        const uploadState = row.original.uploadState;
        const fileName = getOriginalFileName(row);
        const fileExtension = getFileExtension(fileName);
        const isSendFile = sendFileExtensions.includes(fileExtension);

        if (isSendFile) {
          return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%" }}>
              <CircularProgress size={20} sx={{ color: "#6036D6" }} />
            </Box>
          );
        }

        const progress = Math.max(
          0,
          Math.min(uploadState.progress ?? 0, 100)
        );

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
            }}
          >
            <Box sx={{ flex: 1 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 3,
                  backgroundColor: "#E0E0FF",
                  [`& .${linearProgressClasses.bar}`]: {
                    backgroundColor: "#6036D6",
                  },
                }}
              />
            </Box>

            <Typography
              sx={{
                fontSize: 12,
                color: "#6036D6",
                fontWeight: 600,
                minWidth: 36,
              }}
            >
              {progress}%
            </Typography>

            <Tooltip title="Cancel Upload">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  cancelUpload?.(uploadState.uploadId);
                }}
                sx={{ color: "#757575", p: 0.5 }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      }

      if (row.original?.uploadState?.status === "failed") {
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                color: "#d32f2f",
              }}
            >
              Failed
            </Typography>

            <Tooltip title="Retry">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRetry?.(row.original.uploadState);
                }}
                sx={{ color: "#6036D6", p: 0.5 }}
              >
                <ReplayIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Remove">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  cancelUpload?.(row.original.uploadState.uploadId);
                }}
                sx={{ color: "#757575", p: 0.5 }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      }
      const formatTime = (time: number) => {
        const hours = Math.floor(time / 3600);
        const minutes = Math.floor((time % 3600) / 60);
        const seconds = time % 60;
        return `${hours}:${minutes}:${seconds}`;
      };
      const disabledStyle = {
        opacity: 0.4,
        pointerEvents: "none",
        cursor: "not-allowed",
      };
      const fileUrl =
        row.original?.type === "attachment"
          ? row.original?.url || row.original?.file_name
          : row.original?.file_name;
      const isGoogleDoc = fileUrl?.includes("docs.google.com/document");
      const fileName = getOriginalFileName(row);
      const fileExtension = !isGoogleDoc ? getFileExtension(fileName) : "";
      const manualAddedAsset = row.original.asset_source === "Account";

      const isAiEnabled = aiProcessingEnabled && !isGoogleDoc && aiSupportedExtensions.includes(fileExtension.toLowerCase()) && manualAddedAsset;
      const isLink =
        row.original?.c_type?.toLowerCase() === "link" ||
        row.original?.assetType?.toLowerCase() === "link";

      return (
        <Box sx={{ display: "flex", flexDirection: "row", gap: "5px", alignItems: "center", justifyContent: "flex-start", ml: "-10px" }}>
          {!isLink && !isGoogleDoc && (
            <Tooltip title={"Download"} placement="top" arrow>
              <IconButton
                onClick={isInteractionAttendee ? () => handleDownload(row.original?.id || row.id) : undefined}
                sx={!isInteractionAttendee ? disabledStyle : {}}
              >
                <img src={DownloadIcon} alt="download"/>
              </IconButton>
            </Tooltip>
          )}

              <Dialog open={openView} maxWidth="md">
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <IconButton onClick={handleCloseView} size="small" sx={{ backgroundColor:"#F2F2F4", ":hover": { backgroundColor: "#F2F2F4" } }}>
                    <ClearIcon />
                  </IconButton>
                </Box>

                <DialogContent sx={{ marginLeft: 5, fontSize: "15px", fontFamily: 'var(--font-family)' }}>
                  {transcripts && transcripts.length > 0 ? (
                    transcripts.map((item: any, index: number) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          marginBottom: "8px",
                        }}
                      >
                        <Box sx={{ minWidth: "220px", fontWeight: "bold" }}>
                          {item.speaker}{item.start !== undefined && item.start !== null ? `: ${formatTime(item.start)}` : ''}
                        </Box>
                        <Box>- {item.text}</Box>
                      </Box>
                    ))
                  ) : row.original?.transcript ? (
                    <Box sx={{ whiteSpace: "pre-wrap" }}>{row.original.transcript}</Box>
                  ) : null}
                </DialogContent>
              </Dialog>

          {/* Delete Option */}
          {row.original.uploaded_by === loggedInEmail && row.original.asset_source === "Account" && (
            <Tooltip title="Delete" placement="top" arrow>
              <IconButton>
              <img
                src={Delete}
                alt="Delete Collateral"
                style={{ 
                  width: "20px", 
                  height: "18px", 
                  cursor: isInteractionAttendee ? "pointer" : "not-allowed",
                  opacity: isInteractionAttendee ? 1 : 0.4,
                  pointerEvents: isInteractionAttendee ? "auto" : "none",
                 }}
                onClick={
                  isInteractionAttendee
                    ? () => {
                        setDeleteCollateralId(row.original?.id || row.id);
                        setOpenConfirmDialog(true);
                      }
                    : undefined
                }
              />
              </IconButton>
            </Tooltip>
          )}
          {(() => {
            if (!aiProcessingEnabled) return null;

            const groupId = row.original?.interactionGroupId;
            const groupStatuses: string[] = groupId ? groupStatusMap[groupId] || [] : [];
            const ownStatus = row.original?.aiAnalysisStatus;
            const statuses = groupStatuses.length > 0 ? groupStatuses : [ownStatus];

            const isAiProcessingInProgress = isGroupAiInProgress(statuses);
            const isAiProcessingCompleted = isGroupAiCompleted(statuses);

            if (isAiProcessingInProgress) {
              return (
                <Tooltip title="AI processing will take up to 120 mins" placement="top" arrow>
                  <IconButton>
                    <img src={AiLoadingIcon} alt="In Progress" />
                  </IconButton>
                </Tooltip>
              );
            }

            if (isAiProcessingCompleted) {
              return (
                <Tooltip title="AI Processing Completed" placement="top" arrow>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      const groupId = row.original?.interactionGroupId;
                      if (onNavigateToRecording) {
                        onNavigateToRecording(groupId);
                      }
                    }}
                  >
                    <img
                      src={AiIcon}
                      alt="AI Processing"
                      style={{ width: "20px", height: "20px" }}
                    />
                  </IconButton>
                </Tooltip>
              );
            }

            return (
              isAiEnabled && (
                <Tooltip title="Analyze with AI" placement="top" arrow>
                  <IconButton
                    onClick={
                      isInteractionAttendee && handleOpenAiProcessing
                        ? (e) => {
                            e.stopPropagation();
                            handleOpenAiProcessing(row);
                          }
                        : undefined
                    }
                    sx={!isInteractionAttendee ? disabledStyle : {}}
                  >
                    <img
                      src={AiProcessingIcon}
                      alt="AI Processing"
                      style={{ width: "20px", height: "20px" }}
                    />
                  </IconButton>
                </Tooltip>
              )
            );
          })()}
        </Box>
      );
    },
  },
  ];
};
